import { Flex, Heading, SimpleGrid, Text } from '@chakra-ui/react';
import { allBusinessPartners, allSectors, SectorInfo } from '@features/supply/fakeSupplyChainData';
import { useHref } from 'hooks/useHref';
import Link from 'next/link';
import { memo, useMemo } from 'react';

const BADGE_COLORS = [
  '#B91C1C', // red
  '#15803D', // green
  '#CA8A04', // yellow
  '#1D4ED8', // blue
  '#7C3AED', // purple
  '#0891B2', // cyan
  '#C2410C', // orange
  '#BE185D', // pink
];

interface SectorCardProps {
  sector: SectorInfo;
  partnerInitials: string[];
  totalItems: number;
  overflow: number;
}

const SectorCard = memo(({ sector, partnerInitials, totalItems, overflow }: SectorCardProps) => {
  return (
    <Flex
      direction="column"
      gap={3}
      p={4}
      bg="surface.background"
      borderRadius="xl"
      borderColor="border.light"
      borderWidth="1px"
      minH="100px"
      _hover={{ bg: 'surface.hover' }}
      cursor="pointer"
      transition="background 0.15s"
    >
      <Flex>
        {partnerInitials.map((initials, i) => (
          <Flex
            key={i}
            w="28px"
            h="28px"
            borderRadius="full"
            bg={BADGE_COLORS[i % BADGE_COLORS.length]}
            alignItems="center"
            justifyContent="center"
            ml={i === 0 ? '0' : '-4px'}
            zIndex={partnerInitials.length - i}
            boxShadow="0 0 0 2px var(--chakra-colors-surface-background)"
          >
            <Text fontSize="xs" fontWeight="bold" color="white">
              {initials}
            </Text>
          </Flex>
        ))}
        {overflow > 0 && (
          <Flex
            w="28px"
            h="28px"
            borderRadius="full"
            bg="gray.500"
            alignItems="center"
            justifyContent="center"
            ml="-4px"
            zIndex={0}
            boxShadow="0 0 0 2px var(--chakra-colors-surface-background)"
          >
            <Text fontSize="2xs" fontWeight="bold" color="white">
              +{overflow}
            </Text>
          </Flex>
        )}
      </Flex>
      <Flex justifyContent="space-between" alignItems="flex-end">
        <Heading size="md">{sector.label}</Heading>
        <Flex direction="column" alignItems="flex-end">
          <Heading size="2xl">{totalItems}</Heading>
          <Text fontSize="xs" color="text.secondary">
            {totalItems === 1 ? 'item' : 'items'} in demand
          </Text>
        </Flex>
      </Flex>
    </Flex>
  );
});
SectorCard.displayName = 'SectorCard';

const SectorCards = () => {
  const { supplySectorHref } = useHref();

  const sectorData = useMemo(() => {
    const map: Record<string, { initials: string[]; totalItems: number; overflow: number }> = {};
    for (const sector of allSectors) {
      const partners = allBusinessPartners.filter((bp) => bp.sector === sector.id);
      const initials = partners.slice(0, 4).map((bp) => bp.initials);
      const overflow = Math.max(0, partners.length - 4);
      const totalItems = partners.reduce((sum, bp) => sum + bp.inventoryNeeds.length, 0);
      map[sector.id] = { initials, totalItems, overflow };
    }
    return map;
  }, []);

  return (
    <Flex
      direction="column"
      gap={4}
      p={4}
      background="linear-gradient(rgba(196, 154, 60, 0.16) 0%, rgba(196, 154, 60, 0) 100%)"
      borderRadius="xl"
      borderColor="border.light"
      borderWidth="1px"
      w="full"
    >
      <Heading>Sectors</Heading>
      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={4}>
        {allSectors.map((sector) => (
          <Link key={sector.id} href={supplySectorHref(sector.id)}>
            <SectorCard
              sector={sector}
              partnerInitials={sectorData[sector.id]?.initials ?? []}
              totalItems={sectorData[sector.id]?.totalItems ?? 0}
              overflow={sectorData[sector.id]?.overflow ?? 0}
            />
          </Link>
        ))}
      </SimpleGrid>
    </Flex>
  );
};

export default memo(SectorCards);
